import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "store";
import { type Dish, type Order, type Refill, TransactionMode, TransactionState } from "types";
import { safeParseNumber } from "utils/mealListUtils";

// The single transaction the UI is currently following — at most one of order/refill at a time.
export type TransactionSlot = { kind: "order"; transaction: Order } | { kind: "refill"; transaction: Refill } | null;

// One subscription push: the BE reports a flow in EITHER `orders.last` OR `refills.last`
// (the same flow can migrate between fields), and both may echo stale finished transactions.
export type TransactionFrame = { order?: Order | null; refill?: Refill | null };

// "Active" for slot ownership means any non-Finished state — StockCheck still owns the slot.
const isActive = (transaction: Order | Refill) => transaction.state !== TransactionState.Finished;
const orderSlot = (transaction: Order): TransactionSlot => ({ kind: "order", transaction: transaction });
const refillSlot = (transaction: Refill): TransactionSlot => ({ kind: "refill", transaction: transaction });

// True only while a non-finished ORDER owns the slot. The dish reconcile uses this to pick frame
// semantics: during an order the BE streams SPARSE `dishes.types` deltas (absence = unchanged → merge
// only), while order-finish and browse send FULL snapshots (absence = the last dish was taken → drop
// the type). During a refill the inventory ledger is the stock authority (see applyRefillInventory /
// applyFridgeFrame). See setDishTypes(reconcileRemovals) in dishesSlice and the routing in DeviceContext.
export const isActiveOrderSlot = (slot: TransactionSlot): boolean =>
	slot?.kind === "order" && isActive(slot.transaction);

// True while a non-Finished REFILL owns the slot. During an active refill the transaction's
// inventory ledger (applyRefillInventory) is the single stock authority and dish frames feed
// lighting only (JID-2303). Deliberately mode-agnostic: local and remote refills are identical.
// Deliberately NOT a type predicate: `false` does not imply "not a refill slot" (a FINISHED
// refill still has kind: "refill"), so negative narrowing would lie. Narrow via `kind` first.
export const isActiveRefillSlot = (slot: TransactionSlot): boolean =>
	slot?.kind === "refill" && isActive(slot.transaction);

// Pure resolver: given the slot we currently hold and a fresh frame, decide which transaction
// owns the slot. Rules R1–R7 below are ordered by priority.
export const pickTransaction = (current: TransactionSlot, frame: TransactionFrame): TransactionSlot => {
	// R1: an active refill (local or remote) always wins — the backend forbids starting a new
	// transaction while one is active, so an active refill in the frame means anything else is stale.
	if (frame.refill && isActive(frame.refill)) {
		return refillSlot(frame.refill);
	}
	// R2: otherwise an active order wins for the same reason.
	if (frame.order && isActive(frame.order)) {
		return orderSlot(frame.order);
	}
	// Only finished (or absent) candidates remain past this point.
	// R3: a frame payload with the slot's own id is an update to the transaction we already follow
	// (e.g. Active → Finished) — always land it so the flow can reach its summary step.
	if (current?.kind === "order" && frame.order && frame.order.id === current.transaction.id) {
		return orderSlot(frame.order);
	}
	if (current?.kind === "refill" && frame.refill && frame.refill.id === current.transaction.id) {
		return refillSlot(frame.refill);
	}
	// R4: the BE reports a flow in either field — when we hold a RUNNING order but the frame carries
	// only a LOCAL refill, the flow moved to `refills`, so the held order is stale and would mask the
	// refill (order takes precedence in selectActiveLocalTransaction), hanging the UI on OrderStep3.
	// Remote refills are excluded: they run in their own modal and never continue an order flow.
	// A FINISHED order is excluded too: it owns the summary screen until reset, and a stale
	// refill echo must not steal it (R7's philosophy).
	if (
		current?.kind === "order" &&
		isActive(current.transaction) &&
		frame.refill &&
		frame.refill.mode !== TransactionMode.Remote &&
		!frame.order
	) {
		return refillSlot(frame.refill);
	}
	// R5: we hold an Active transaction but the frame's same-kind candidate has a different id —
	// the held Active payload is stale and the candidate takes the slot. Covers the reconnect gap
	// (we missed the held transaction's finish while offline) as well as any same-kind churn.
	if (current && isActive(current.transaction)) {
		if (current.kind === "order" && frame.order && frame.order.id !== current.transaction.id) {
			return orderSlot(frame.order);
		}
		if (current.kind === "refill" && frame.refill && frame.refill.id !== current.transaction.id) {
			return refillSlot(frame.refill);
		}
	}
	// R6: empty slot — take whatever the frame offers, order first (matches the order precedence
	// in selectActiveLocalTransaction), so the UI can show the last finished transaction's summary.
	if (current === null) {
		if (frame.order) {
			return orderSlot(frame.order);
		}
		if (frame.refill) {
			return refillSlot(frame.refill);
		}
		return null;
	}
	// R7: an occupied slot keeps against stale strangers — a finished different-kind echo or an
	// empty frame must not knock out the transaction whose summary the user may still be viewing.
	return current;
};

// Scan interval of the order currently held in the slot, clocked on the terminal (JID-2348):
// the BE schema carries no stock-check timestamp and the subscription requests only `state`.
// `stockCheckAt` is stamped by the frame that moves the HELD order ACTIVE → STOCK_CHECK,
// `finishedAt` by the first frame landing it FINISHED. Feeds $scanning_duration_ms on the
// finished-order events; it replaced the scanning-started/-ended events.
export type ScanTiming = { orderId: string; stockCheckAt: number; finishedAt: number | null };

// How a transaction payload reached the slot. "live": a subscription frame, seen as it happened.
// "snapshot": the initial-state query — on boot, but also on reconnect recovery while the pre-outage
// slot is still held, so a transition the snapshot reveals happened at an unknown time during the
// outage and must not be stamped into the scan timing (MR260 review).
export type TransactionObservation = "live" | "snapshot";

// Pure resolver, the timing counterpart of pickTransaction: given the slot we held, the slot the
// frame resolved to, the timing we hold and WHEN the frame was observed (null = a snapshot: the
// transition's time is unknown), decide the next timing. Rules T1–T5 (see nextScanTiming.test.ts):
//   T1 nextSlot is null or a refill → null (only orders are timed).
//   T2 a timing for a different order id is discarded before applying T3–T5.
//   T3 the HELD order (same id in prevSlot) in a non-STOCK_CHECK, non-FINISHED state moves to
//      STOCK_CHECK and no timing exists yet → { orderId, stockCheckAt: observedAt, finishedAt: null }.
//      A first observation that is already STOCK_CHECK (reload, R5 eviction) therefore yields no
//      start, and so does a snapshot (observedAt null): the property is omitted, never reported short.
//   T4 a timing for this id with finishedAt === null and nextSlot is FINISHED → finishedAt = observedAt;
//      from a snapshot the finish time is unknown, so the open timing is discarded instead.
//   T5 otherwise return prevTiming unchanged (re-delivered frames and no-transition snapshots never
//      re-stamp; first wins).
export const nextScanTiming = (
	prevSlot: TransactionSlot,
	nextSlot: TransactionSlot,
	prevTiming: ScanTiming | null,
	observedAt: number | null,
): ScanTiming | null => {
	// T1: only orders are timed.
	if (nextSlot === null || nextSlot.kind !== "order") {
		return null;
	}
	const next = nextSlot.transaction;
	// T2: a timing left over from a different order never carries across.
	const timing = prevTiming?.orderId === next.id ? prevTiming : null;
	// T3: the scan starts when the order we already HELD leaves its pre-scan state for STOCK_CHECK.
	// Requiring the held pre-scan state is what excludes first observations that are already
	// STOCK_CHECK (reload, R5 eviction): they have no start, so the property is omitted rather than
	// reported too short. A snapshot revealing the transition has no known time either → no start.
	const held = prevSlot?.kind === "order" && prevSlot.transaction.id === next.id ? prevSlot.transaction : null;
	const heldBeforeScan =
		held !== null && held.state !== TransactionState.StockCheck && held.state !== TransactionState.Finished;
	if (timing === null && heldBeforeScan && next.state === TransactionState.StockCheck) {
		return observedAt === null ? null : { orderId: next.id, stockCheckAt: observedAt, finishedAt: null };
	}
	// T4: the first FINISHED frame ends the scan. Learned from a snapshot, the finish fell somewhere
	// inside the outage — the measurement is lost, so drop the open timing rather than absorb the gap.
	if (timing !== null && timing.finishedAt === null && next.state === TransactionState.Finished) {
		return observedAt === null ? null : { ...timing, finishedAt: observedAt };
	}
	// T5: re-delivered frames never re-stamp — the first observation wins, and the same object is
	// returned so unchanged frames leave the Redux state referentially stable.
	return timing;
};

type TransactionSlice = {
	slot: TransactionSlot;
	currentOrderStep: number;
	scanTiming: ScanTiming | null;
};

const initialState: TransactionSlice = {
	slot: null,
	currentOrderStep: 1,
	scanTiming: null,
};

export const transactionSlice = createSlice({
	name: "transaction",
	initialState: initialState,
	reducers: {
		setTransaction: {
			// The observation clock is read in `prepare` — RTK's home for impure action inputs — so
			// the reducer stays pure and replayable; tests drive it with vi.setSystemTime. Every
			// writer of the slot (subscription frames, the initial-state snapshot, the debug modal)
			// goes through this one action, so the scan timing is kept consistently for all of them.
			// A snapshot carries no clock: the transition it reveals has no known time.
			prepare: (frame: TransactionFrame, observation: TransactionObservation = "live") => ({
				payload: frame,
				meta: { observedAt: observation === "live" ? Date.now() : null },
			}),
			reducer: (state, { payload, meta }: PayloadAction<TransactionFrame, string, { observedAt: number | null }>) => {
				const next = pickTransaction(state.slot, payload);
				state.scanTiming = nextScanTiming(state.slot, next, state.scanTiming, meta.observedAt);
				state.slot = next;
			},
		},
		resetTransaction: (state) => {
			state.slot = null;
			state.currentOrderStep = 1;
			state.scanTiming = null;
		},
		clearTransactionSlot: (state) => {
			state.slot = null;
			state.scanTiming = null;
		},
		updateCurrentOrderStep: (state, action: PayloadAction<number>) => {
			state.currentOrderStep = action.payload;
		},
	},
});

export const { clearTransactionSlot, resetTransaction, setTransaction, updateCurrentOrderStep } =
	transactionSlice.actions;

const selectSlot = (state: RootState): TransactionSlot => state.transaction?.slot ?? null;
const selectScanTiming = (state: RootState): ScanTiming | null => state.transaction?.scanTiming ?? null;

// Integer milliseconds between the two scan stamps of the held order, for $scanning_duration_ms.
// Null while the scan is unfinished or its start was never observed (the property is then omitted
// from the finished-order event), and null on a negative interval — a clock that ran backwards
// between the stamps produced a corrupt measurement, not a 0 ms scan.
export const selectScanningDurationMs = createSelector(selectScanTiming, (timing): number | null => {
	if (timing === null || timing.finishedAt === null) {
		return null;
	}
	const duration = timing.finishedAt - timing.stockCheckAt;
	return duration >= 0 ? duration : null;
});

export const selectOrder = createSelector(selectSlot, (slot): Order | null =>
	slot?.kind === "order" ? slot.transaction : null,
);
// Primitive projection of the order's state. Every subscription frame replaces the order object (new
// reference), so subscribing to the whole order re-renders a component every frame; subscribing to this
// enum re-renders it only when the state actually transitions. For per-item components (e.g. DishTypeItem)
// that care only about the state, not the order payload.
export const selectOrderState = createSelector(selectOrder, (order): TransactionState | null => order?.state ?? null);
export const selectRefill = createSelector(selectSlot, (slot): Refill | null =>
	slot?.kind === "refill" ? slot.transaction : null,
);
export const selectHasActiveOrder = createSelector(selectOrder, (order) => order !== null);
export const selectUserPreferences = createSelector(selectOrder, (order) => order?.user?.preferences || null);
export const selectOrderPaymentMethod = createSelector(selectOrder, (order) => order?.payment?.method?.type || null);
export const selectRefillMode = createSelector(selectRefill, (refill): TransactionMode | null => {
	if (!refill || refill.state === TransactionState.Finished) {
		return null;
	}
	return refill.mode || null;
});
// Remote refills are owned by RemoteRefillModal, so they never surface here. Finished local
// transactions stay in the slot for the OrderStep5 summary until resetTransaction clears it.
export const selectActiveLocalTransaction = createSelector(selectSlot, (slot): Order | Refill | null => {
	if (slot === null) {
		return null;
	}
	if (slot.kind === "refill" && slot.transaction.mode === TransactionMode.Remote) {
		return null;
	}
	return slot.transaction;
});
// Reads the slot's `kind`, not `type`: frames are sparse deltas, so a refill can arrive with `type: null`.
// Remote refills are excluded, as in selectActiveLocalTransaction.
export const selectIsLocalRefill = createSelector(
	selectSlot,
	(slot): boolean => slot?.kind === "refill" && slot.transaction.mode !== TransactionMode.Remote,
);
// True while an order or local refill is RUNNING (not Finished). A boolean so subscribers
// re-render on the running/idle edge only, not on every subscription frame that lands a
// fresh transaction payload in the slot.
export const selectIsLocalTransactionActive = createSelector(
	selectActiveLocalTransaction,
	(transaction) => transaction !== null && isActive(transaction),
);
// True while a refill owns the slot, in any state. A boolean so DrawerContext can skip the order-style
// step-5 snackbar for refills without subscribing to the refill payload (JID-2360).
export const selectIsRefillSlot = createSelector(selectSlot, (slot): boolean => slot?.kind === "refill");
// The refill summary owns step 5 only for a FINISHED LOCAL refill (JID-2360). A string|null projection
// (not the transaction) so useOrderSteps does not re-render on every subscription frame, and so the id can
// key the modal: a different finished refill taking the slot remounts it with a fresh countdown.
export const selectFinishedLocalRefillId = createSelector(selectSlot, (slot): string | null => {
	if (slot?.kind !== "refill") {
		return null;
	}
	const refill = slot.transaction;
	return refill.mode !== TransactionMode.Remote && refill.state === TransactionState.Finished ? refill.id : null;
});

// Unlike selectActiveLocalTransaction, these cover EVERY transaction kind in the slot — including
// remote refills (which RemoteRefillModal owns and the "local" selectors deliberately hide). Used by
// the QR-lifecycle logging, which must observe start/end for orders, local refills and remote refills.
export type TransactionDescriptor = { id: string; type: "order" | "local-refill" | "remote-refill" };
export const selectAnyTransaction = createSelector(
	selectSlot,
	(slot): Order | Refill | null => slot?.transaction ?? null,
);
export const selectIsAnyTransactionActive = createSelector(
	selectAnyTransaction,
	(transaction) => transaction !== null && isActive(transaction),
);
export const selectTransactionDescriptor = createSelector(selectSlot, (slot): TransactionDescriptor | null => {
	if (slot === null) {
		return null;
	}
	if (slot.kind === "order") {
		return { id: slot.transaction.id, type: "order" };
	}
	return {
		id: slot.transaction.id,
		type: slot.transaction.mode === TransactionMode.Remote ? "remote-refill" : "local-refill",
	};
});
export const selectCurrentOrderStep = (state: RootState) => state.transaction.currentOrderStep;
// Sourced from the active local transaction so refill step 3/5 drawers see removed dishes too.
export const selectTransactionDishes = createSelector(
	selectActiveLocalTransaction,
	(transaction) => transaction?.inventory?.removed || [],
);
export const selectIsEmptyOrder = createSelector(selectActiveLocalTransaction, (transaction) => {
	if (!transaction) {
		return false;
	}
	const hasEmptyInventory = transaction.inventory?.removed?.length === 0 || !transaction.inventory;
	return hasEmptyInventory && transaction.state === TransactionState.Finished;
});

// A dish carrying `price.default` is discounted — `default` holds the pre-discount price.
const isDiscounted = (dish: Dish) => dish.price?.default != null;

const countByDishType = (dishes: Dish[]): Record<string, number> =>
	dishes.reduce(
		(acc, dish) => {
			const dishTypeId = dish.type?.id;
			if (dishTypeId) {
				acc[dishTypeId] = (acc[dishTypeId] || 0) + 1;
			}
			return acc;
		},
		{} as Record<string, number>,
	);

// TODO: add fetching logic for when there are unknown dish types in the order
export const selectOrderDishTypes = createSelector(
	[(state: RootState) => state.dishes.types, selectActiveLocalTransaction],
	(fridgeDishes, transaction) => {
		if (!(fridgeDishes && transaction?.inventory?.removed)) {
			return [];
		}
		const orderDishes = transaction.inventory.removed;
		const fullPriceDishes = orderDishes.filter((dish) => !isDiscounted(dish));
		const orderDishTypeIds = new Set(fullPriceDishes.map((dish) => dish?.type?.id));
		// Keyed over ALL removed dishes (not just full-price ones) — a later discounted dish of the
		// same type intentionally overrides the price shown for that type.
		const orderDishesById = new Map(orderDishes.map((dish) => [dish.type?.id, dish]));
		return fridgeDishes
			.filter((dishType) => orderDishTypeIds.has(dishType.id))
			.map((dishType) => {
				const orderDish = orderDishesById.get(dishType.id);
				return {
					...dishType,
					...(orderDish?.price && {
						price: { ...dishType.price, ...orderDish.price },
					}),
				};
			});
	},
);

export const selectDiscountedOrderDishTypes = createSelector(
	[(state: RootState) => state.dishes.types, selectActiveLocalTransaction],
	(fridgeDishes, transaction) => {
		if (!(fridgeDishes && transaction?.inventory?.removed)) {
			return [];
		}
		const discountedDishes = transaction.inventory.removed.filter(isDiscounted);
		const discountedDishTypeIds = new Set(discountedDishes.map((dish) => dish?.type?.id));
		const discountedDishesById = new Map(discountedDishes.map((dish) => [dish.type?.id, dish]));

		return fridgeDishes
			.filter((dishType) => discountedDishTypeIds.has(dishType.id))
			.map((dishType) => {
				const discountedDishPrice = discountedDishesById.get(dishType.id)?.price;
				return {
					...dishType,
					// The dish's own price replaces (not merges) the catalog price — it already
					// carries `default` (the original price) alongside the discounted value.
					...(discountedDishPrice && {
						price: { ...discountedDishPrice },
					}),
				};
			});
	},
);

export type DiscountCodeState = {
	name: string | null;
	currency: string | null;
	total: number;
	applied: number;
	unused: number;
};

// The unused amount is derived on the client on purpose (JID-2279 D-10): don't add it to the GraphQL schema.
export const selectDiscountCodeState = createSelector(selectOrder, (order): DiscountCodeState | null => {
	const discountCode = order?.discountCode;
	if (!discountCode) {
		return null;
	}
	const total = safeParseNumber(discountCode.amount?.total);
	if (total <= 0) {
		return null;
	}
	const applied = Math.min(total, Math.max(0, safeParseNumber(discountCode.amount?.applied)));
	return {
		name: discountCode.name ?? discountCode.code,
		currency: order?.price?.total?.currency ?? null,
		total: total,
		applied: applied,
		unused: total - applied,
	};
});

export const selectOrderSummary = createSelector(
	selectActiveLocalTransaction,
	selectOrder,
	selectOrderDishTypes,
	selectDiscountedOrderDishTypes,
	(transaction, order, orderDishTypes, discountedDishTypes) => {
		if (!transaction) {
			return null;
		}

		const orderDishes = transaction.inventory?.removed || [];
		const orderDishTypeIds = new Set(orderDishes.map((dish) => dish.type?.id).filter((id) => id != null));

		return {
			orderDishTypesCount: orderDishTypeIds.size,
			orderDishTypes: orderDishTypes,
			discountedDishTypes: discountedDishTypes,
			// Refills don't carry a price; OrderPriceSummary tolerates undefined.
			orderPrice: order?.price,
			fullPricedDishCounts: countByDishType(orderDishes.filter((dish) => !isDiscounted(dish))),
			discountedDishCounts: countByDishType(orderDishes.filter(isDiscounted)),
		};
	},
);

export default transactionSlice.reducer;
